import { useMemo, useState } from 'react';
import SectionHeading from '@/pages/home/components/SectionHeading';
import { clueCategories, clueItems } from '@/mocks/homeData';

interface ClueBoardProps {
  query: string;
  onQueryChange: (value: string) => void;
}

const stageStyle: Record<string, string> = {
  已比对: 'border-secondary-200 bg-secondary-100 text-secondary-900',
  待核验: 'border-primary-200 bg-primary-100 text-primary-800',
  线索中: 'border-background-300 bg-background-100 text-foreground-700',
};

export default function ClueBoard({ query, onQueryChange }: ClueBoardProps) {
  const [active, setActive] = useState('all');
  const [sort, setSort] = useState('heat');

  const list = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    const filtered = clueItems.filter((item) => {
      const matchCategory = active === 'all' || item.category === active;
      if (!matchCategory) return false;
      if (!keyword) return true;
      return (
        item.title.toLowerCase().includes(keyword) ||
        item.summary.toLowerCase().includes(keyword) ||
        item.code.toLowerCase().includes(keyword) ||
        item.tags.join('').toLowerCase().includes(keyword)
      );
    });

    return [...filtered].sort((a, b) => {
      if (sort === 'evidence') return b.evidence - a.evidence;
      if (sort === 'time') return a.time < b.time ? 1 : -1;
      return b.heat - a.heat;
    });
  }, [active, query, sort]);

  return (
    <section id="clues" className="scroll-mt-20 border-b border-background-200 bg-background-50">
      <div className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-6 md:py-16">
        <SectionHeading
          index="01"
          eyebrow="Clue Stream"
          title="线索速览"
          desc="每条线索都标注了所属证据类型、当前核验状态与关联材料数量。切换分类或直接检索，快速定位你关心的那一条。"
        />

        <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100/60 p-3 md:flex-row md:items-center md:justify-between md:p-3.5">
          <div className="flex flex-wrap items-center gap-1 rounded-full border border-background-200 bg-background-50 p-1">
            {clueCategories.map((category) => (
              <button
                key={category.id}
                type="button"
                onClick={() => setActive(category.id)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 font-label text-xs transition-colors md:px-4 ${
                  active === category.id
                    ? 'bg-foreground-950 text-background-50'
                    : 'text-foreground-600 hover:text-foreground-950'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2">
              <i className="ri-search-line text-sm leading-none text-foreground-400"></i>
              <input
                value={query}
                onChange={(e) => onQueryChange(e.target.value)}
                placeholder="关键词 / 编号"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400 sm:w-40"
              />
            </div>
            <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2">
              <i className="ri-sort-desc text-sm leading-none text-foreground-400"></i>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="cursor-pointer bg-transparent text-sm text-foreground-900 outline-none"
              >
                <option value="heat">按热度</option>
                <option value="time">按时间</option>
                <option value="evidence">按材料数</option>
              </select>
            </div>
          </div>
        </div>

        <div className="mt-5 flex items-center justify-between">
          <p className="font-label text-xs text-foreground-500">
            共 <span className="font-medium text-foreground-900">{list.length}</span> 条线索
          </p>
          <span className="hidden items-center gap-1.5 font-label text-xs text-foreground-500 sm:flex">
            <i className="ri-database-2-line text-sm leading-none"></i>
            数据为公开资料整理示例
          </span>
        </div>

        {list.length === 0 ? (
          <div className="mt-4 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16">
            <i className="ri-file-unknow-line text-3xl leading-none text-foreground-300"></i>
            <p className="mt-3 text-sm text-foreground-600">没有匹配的线索，换个关键词试试</p>
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {list.map((item, index) => (
              <article
                key={item.id}
                className="group flex h-full cursor-pointer flex-col rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-foreground-300 hover:bg-background-100/70"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-label text-[11px] tracking-wider text-foreground-400">
                    #{item.code}
                  </span>
                  <span
                    className={`rounded-full border px-2 py-0.5 font-label text-[11px] ${
                      stageStyle[item.stage] ?? stageStyle['线索中']
                    }`}
                  >
                    {item.stage}
                  </span>
                </div>

                <div className="mt-3 flex items-start gap-2">
                  <span className="mt-0.5 font-heading text-sm font-bold text-foreground-300">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <h3 className="font-heading text-[15px] font-bold leading-snug text-foreground-950 transition-colors group-hover:text-accent-600">
                    {item.title}
                  </h3>
                </div>

                <p className="mt-2 flex-1 text-[13px] leading-6 text-foreground-600">{item.summary}</p>

                <div className="mt-3 flex flex-wrap gap-1.5">
                  {item.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-md bg-secondary-100 px-2 py-0.5 font-label text-[11px] text-secondary-900"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 font-label text-[11px] text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line text-sm leading-none"></i>
                    {item.time}
                  </span>
                  <span className="flex items-center gap-3">
                    <span className="flex items-center gap-1">
                      <i className="ri-fire-line text-sm leading-none text-accent-500"></i>
                      {item.heat.toLocaleString()}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-attachment-2 text-sm leading-none"></i>
                      {item.evidence}
                    </span>
                  </span>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}